import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { ISessionRequest, IUserRequest } from '@/data/_autogen/IValveVista';
import { TokenType } from '@/data/_autogen/IValveVista';
import AuthService from '@/services/AuthService';

const STORAGE_KEY = 'valve_vista_auth';

export const useAuthStore = defineStore('auth', () => {
  // ============================================================================
  // STATE
  // ============================================================================

  const token = ref<string | null>(null);
  const tokenType = ref<TokenType>(TokenType.None);
  const userId = ref<string | null>(null);
  const userName = ref<string | null>(null);
  const firstName = ref<string | null>(null);
  const lastName = ref<string | null>(null);
  const emailAddress = ref<string | null>(null);
  const sessionId = ref<string | null>(null);
  const expiresInSeconds = ref<number | null>(null);
  const expirationTime = ref<Date | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const lastGuestTokenRequest = ref<Date | null>(null);

  // ============================================================================
  // GETTERS (COMPUTED PROPERTIES)
  // ============================================================================

  /**
   * Check if user is authenticated (has valid token)
   */
  const isAuthenticated = computed(() => {
    return (
      token.value !== null &&
      (tokenType.value === TokenType.AccessToken)
    );
  });

  /**
   * Check if current auth is guest session
   */
  const isGuest = computed(() => {
    return tokenType.value === TokenType.GuestToken;
  });

  /**
   * Check if current auth is registered user
   */
  const isRegisteredUser = computed(() => {
    return tokenType.value === TokenType.AccessToken && userId.value !== null;
  });

  /**
   * Get full user name
   */
  const fullName = computed(() => {
    if (!firstName.value && !lastName.value) {
      return null;
    }
    return `${firstName.value} ${lastName.value}`.trim();
  });

  /**
   * Get display name (full name or guest)
   */
  const displayName = computed(() => {
    if (isRegisteredUser.value && fullName.value) {
      return fullName.value;
    }
    if (isGuest.value) {
      return 'Guest';
    }
    return null;
  });

  /**
   * Check if session is expired
   */
  const isTokenExpired = computed(() => {
    if (!expirationTime.value) {
      return false;
    }
    return new Date() > expirationTime.value;
  });

  /**
   * Get header icon color based on auth state
   * gray = no auth, orange = guest, green = registered user
   */
  const headerIconColor = computed(() => {
    if (isRegisteredUser.value) {
      return 'text-green-500'; // Authenticated user
    }
    if (isGuest.value) {
      return 'text-orange-500'; // Guest
    }
    return 'text-gray-400'; // No auth
  });

  /**
   * Get time remaining until token expires (in seconds)
   */
  const timeUntilExpiration = computed(() => {
    if (!expirationTime.value) {
      return null;
    }
    const now = new Date();
    const diff = Math.floor(
      (expirationTime.value.getTime() - now.getTime()) / 1000
    );
    return Math.max(0, diff);
  });

  /**
   * Get formatted expiration time
   */
  const formattedExpirationTime = computed(() => {
    if (!expirationTime.value) {
      return null;
    }
    return expirationTime.value.toLocaleTimeString();
  });

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Request guest token for unauthenticated users
   */
  const requestGuestToken = async () => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await AuthService.getGuestToken();
      const sessionData = response.data;

      // Store guest token data
      token.value = sessionData.token || null;
      tokenType.value = sessionData.tokenType || TokenType.GuestToken;
      sessionId.value = sessionData.userRequest?.sessionId || null;
      lastGuestTokenRequest.value = new Date();

      // Set expiration time
      if (sessionData.expiresInSeconds) {
        expiresInSeconds.value = sessionData.expiresInSeconds;
        expirationTime.value = new Date(
          Date.now() + sessionData.expiresInSeconds * 1000
        );
      }

      syncToLocalStorage();
      return sessionData;
    } catch (err) {
      error.value = 'Failed to request guest token';
      console.error('Guest token request error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Register new user account
   */
  const register = async (userRequest: IUserRequest) => {
    try {
      isLoading.value = true;
      error.value = null;

      // Call registration endpoint
      const response = await AuthService.registerUser(userRequest);

      // After registration, auto-login with the provided credentials
      const loginRequest: IUserRequest = {
        userName: userRequest.userName,
        emailAddress: userRequest.emailAddress,
        password: userRequest.password,
      };

      return await login(loginRequest);
    } catch (err) {
      error.value = 'Failed to register user';
      console.error('Registration error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Login existing user with credentials
   */
  const login = async (userRequest: IUserRequest) => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await AuthService.login(userRequest);
      const sessionData = response.data;

      // Store user token data
      token.value = sessionData.token || null;
      tokenType.value = sessionData.tokenType || TokenType.AccessToken;
      sessionId.value = sessionData.userRequest?.sessionId || null;

      // Store user info
      userId.value = sessionData.userRequest?.userId || null;
      userName.value = sessionData.userRequest?.userName || null;
      firstName.value = sessionData.userRequest?.firstName || null;
      lastName.value = sessionData.userRequest?.lastName || null;
      emailAddress.value = sessionData.userRequest?.emailAddress || null;

      // Set expiration time
      if (sessionData.expiresInSeconds) {
        expiresInSeconds.value = sessionData.expiresInSeconds;
        expirationTime.value = new Date(
          Date.now() + sessionData.expiresInSeconds * 1000
        );
      }

      syncToLocalStorage();
      return sessionData;
    } catch (err) {
      error.value = 'Failed to login';
      console.error('Login error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Logout current user
   */
  const logout = async () => {
    try {
      isLoading.value = true;
      error.value = null;

      // Call logout endpoint
      await AuthService.logout();

      // Clear all auth state
      clearAuth();

      return true;
    } catch (err) {
      error.value = 'Failed to logout';
      console.error('Logout error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Load current session from server
   */
  const loadSession = async () => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await AuthService.me();
      const sessionData = response.data;

      // Update state with session data
      if (sessionData.isGuest) {
        token.value = sessionData.token || null;
        tokenType.value = TokenType.GuestToken;
      } else if (sessionData.token) {
        token.value = sessionData.token;
        tokenType.value = sessionData.tokenType || TokenType.AccessToken;
        userId.value = sessionData.userRequest?.userId || null;
        userName.value = sessionData.userRequest?.userName || null;
        firstName.value = sessionData.userRequest?.firstName || null;
        lastName.value = sessionData.userRequest?.lastName || null;
        emailAddress.value = sessionData.userRequest?.emailAddress || null;
      }

      syncToLocalStorage();
      return sessionData;
    } catch (err) {
      console.error('Load session error:', err);
      // Session load failure is not fatal, continue with existing state
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Clear all auth state
   */
  const clearAuth = () => {
    token.value = null;
    tokenType.value = TokenType.None;
    userId.value = null;
    userName.value = null;
    firstName.value = null;
    lastName.value = null;
    emailAddress.value = null;
    sessionId.value = null;
    expiresInSeconds.value = null;
    expirationTime.value = null;
    error.value = null;
    syncToLocalStorage();
  };

  /**
   * Clear error message
   */
  const clearError = () => {
    error.value = null;
  };

  /**
   * Check if guest token request should be made
   * Returns true if no token exists and user hasn't requested guest token recently
   */
  const shouldRequestGuestToken = (): boolean => {
    if (token.value !== null) {
      return false; // Already have a token
    }

    if (lastGuestTokenRequest.value) {
      // Don't request more than once per minute
      const timeSinceLastRequest =
        (new Date().getTime() - lastGuestTokenRequest.value.getTime()) / 1000;
      return timeSinceLastRequest > 60;
    }

    return true;
  };

  // ============================================================================
  // PERSISTENCE
  // ============================================================================

  /**
   * Save auth state to localStorage
   */
  const syncToLocalStorage = () => {
    const authData = {
      token: token.value,
      tokenType: tokenType.value,
      userId: userId.value,
      userName: userName.value,
      firstName: firstName.value,
      lastName: lastName.value,
      emailAddress: emailAddress.value,
      sessionId: sessionId.value,
      expiresInSeconds: expiresInSeconds.value,
      expirationTime: expirationTime.value?.toISOString(),
      lastGuestTokenRequest: lastGuestTokenRequest.value?.toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));
  };

  /**
   * Load auth state from localStorage
   */
  const loadFromLocalStorage = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return;
    }

    try {
      const authData = JSON.parse(stored);

      token.value = authData.token;
      tokenType.value = authData.tokenType ?? TokenType.None;
      userId.value = authData.userId;
      userName.value = authData.userName;
      firstName.value = authData.firstName;
      lastName.value = authData.lastName;
      emailAddress.value = authData.emailAddress;
      sessionId.value = authData.sessionId;
      expiresInSeconds.value = authData.expiresInSeconds;
      expirationTime.value = authData.expirationTime
        ? new Date(authData.expirationTime)
        : null;
      lastGuestTokenRequest.value = authData.lastGuestTokenRequest
        ? new Date(authData.lastGuestTokenRequest)
        : null;
    } catch (err) {
      console.error('Failed to load auth from localStorage:', err);
    }
  };

  /**
   * Hydrate auth store from localStorage on app init
   */
  const hydrate = () => {
    loadFromLocalStorage();
  };

  /**
   * Reset to initial state (mainly for testing)
   */
  const reset = () => {
    clearAuth();
    localStorage.removeItem(STORAGE_KEY);
  };

  // Return all state, getters, and actions
  return {
    // State
    token,
    tokenType,
    userId,
    userName,
    firstName,
    lastName,
    emailAddress,
    sessionId,
    expiresInSeconds,
    expirationTime,
    isLoading,
    error,
    lastGuestTokenRequest,

    // Getters
    isAuthenticated,
    isGuest,
    isRegisteredUser,
    fullName,
    displayName,
    isTokenExpired,
    headerIconColor,
    timeUntilExpiration,
    formattedExpirationTime,

    // Actions
    requestGuestToken,
    register,
    login,
    logout,
    loadSession,
    clearAuth,
    clearError,
    shouldRequestGuestToken,
    syncToLocalStorage,
    loadFromLocalStorage,
    hydrate,
    reset,
  };
});
